import React, { useCallback, useMemo, useState } from 'react';
import { ApolloQueryResult } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';
import { generatedTypesNew, BillingStatus } from '@css-xp/graphtypes';
import { STATUSPAGE_PATH_2, NEW_DATE_FORMAT } from '@atlassian/cssxp-typescommon';
import capitalize from 'lodash/capitalize';
import CrossCircleIcon from '@atlaskit/icon/glyph/cross-circle';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import VerticalMoreIcon from '@atlaskit/icon/glyph/more-vertical';
import { transformFilterableFields } from '@atlassian/cssxp-utils-generic';
import { routePaths, getRouteUrl } from '../../../route-utils';
import { DynamicResultTable } from '../../../dynamic-result-table';
import { FormattedDate, FormattedAddress } from '../../../formatted';
import { CustomSearch } from '../../../custom-search';
import { CustomFilterWrapper } from '../../../custom-filter';
import { CloudProductDetailView, getBilledForColumnData } from '../../../entitlement-utils';
import { DEFAULT_ROWS_PER_PAGE } from '@atlassian/cssxp-utils-entitlement';
import { useSidebar } from '../../../hooks';
import './subscription.scss';
import { NewBillDate } from '../../../new-bill-date';
import { EntitlementDetails, SearchOptions } from '@css-xp/graphtypes/src/generated-types-new';
import { useApplicationContext } from '../../../common-context';

export interface RefetchOptions {
  searchOptions?: SearchOptions;
  filterOptions?: { key: string; value: string }[];
}
interface SubscriptionTabProps {
  nodes: any;
  totalRecords: number;
  loading: boolean;
  error?: Error;
  label?: string;
  refetch: (_variables?: RefetchOptions) => Promise<ApolloQueryResult<unknown>>;
  isPaymentMethodSidebar?: boolean;
  isShipToPartySidebar?: boolean;
  availableInvoiceGroups?: generatedTypesNew.Maybe<generatedTypesNew.InvoiceGroup[]>;
  setBillDateSidebar?: string;
  invoiceGroupId?: string;
  showFilters?: boolean;
}

interface FormData {
  searchTerm: string;
}

const FILTER_FIELDS = [
  {
    filterItem: 'Status',
    queryKey: 'billingStatus',
    iterableValues: transformFilterableFields<typeof BillingStatus>(BillingStatus),
  },
];

export const SubscriptionTab = ({
  nodes,
  totalRecords,
  loading,
  error,
  refetch,
  label,
  isPaymentMethodSidebar = false,
  isShipToPartySidebar = false,
  availableInvoiceGroups = [],
  setBillDateSidebar,
  invoiceGroupId,
  showFilters = false,
}: SubscriptionTabProps) => {
  /* For PPC (Start) */
  const applicationContext = useApplicationContext();
  const isPartnerAccount = applicationContext?.partnerAccount;
  const [selectedTransactionAccount] = useLocalStorage(`${applicationContext?.atlassianAccountId}-selectedTxa`, '');
  const subscriptionsRoutePPC = getRouteUrl(routePaths.subscriptions, { txaId: selectedTransactionAccount });
  /* For PPC (End) */

  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterOptions, setFilterOptions] = useState<{ key: string; value: string }[]>([]);
  const [searchOptions, setSearchOptions] = useState<{ key: string; value: string }[]>([]);

  const { openSidebar } = useSidebar();

  const subscriptions = useMemo(() => nodes, [nodes]);
  const totalCount = useMemo(() => totalRecords ?? 0, [totalRecords]);
  const headData = useMemo(() => {
    const cells = [
      {
        content: 'Subscription',
        width: 20,
      },
      {
        content: isPaymentMethodSidebar ? 'Sold-to address' : 'Site',
        width: 20,
      },
      {
        content: 'Billing cycle',
        width: 20,
      },
      {
        content: 'Next bill / Expiry',
        width: 20,
      },
      {
        content: 'Billed for',
        width: isShipToPartySidebar || isPaymentMethodSidebar ? 10 : 20,
      },
      {
        content: '',
        width: isShipToPartySidebar || isPaymentMethodSidebar ? 1 : 2,
      },
    ];

    return {
      cells,
    };
  }, [isPaymentMethodSidebar, isShipToPartySidebar]);

  const history = useHistory();

  const getSoldToAddress = useCallback(
    (invoiceGroupId: string | undefined | null) => {
      let currentInvoiceGroupDetails;
      if (!invoiceGroupId) {
        currentInvoiceGroupDetails = availableInvoiceGroups?.[0];
      } else {
        currentInvoiceGroupDetails = availableInvoiceGroups?.find(
          (invoiceGroupObj) => invoiceGroupObj?.id === invoiceGroupId
        );
      }
      if (currentInvoiceGroupDetails) {
        return (
          <div className="line-clamp-sold-to-address">
            <FormattedAddress
              addressLine1={currentInvoiceGroupDetails?.shipToParty?.addressLine1}
              addressLine2={currentInvoiceGroupDetails?.shipToParty?.addressLine2}
              city={currentInvoiceGroupDetails?.shipToParty?.city}
              country={currentInvoiceGroupDetails?.shipToParty?.country}
              postcode={currentInvoiceGroupDetails?.shipToParty?.postcode}
              state={currentInvoiceGroupDetails?.shipToParty?.state}
              organizationName={currentInvoiceGroupDetails?.shipToParty?.name}
            />
          </div>
        );
      }
      return 'No address information';
    },
    [availableInvoiceGroups]
  );

  const getRowAction = useCallback(
    (product: generatedTypesNew.InvoiceGroupProduct) => {
      return (
        <DropdownMenu
          position="bottom right"
          triggerType="button"
          triggerButtonProps={{
            appearance: 'subtle',
            className: 'trigger-button',
            iconBefore: <VerticalMoreIcon label="invoice-action" />,
          }}
          testId={`actions-${product?.id}`}
        >
          <DropdownItemGroup>
            <DropdownItem
              key={'remove-addon'}
              onClick={() => {
                openSidebar({
                  sidebarType: setBillDateSidebar,
                  sidebarOptions: {
                    sidebarWidth: '60%',
                    isOverlay: true,
                  },
                  payload: {
                    product,
                    invoiceGroupId,
                    refetch,
                  },
                });
              }}
            >
              Change bill date
            </DropdownItem>
            <DropdownItem
              key={'add-promotion'}
              onClick={() => {
                history.push(`${STATUSPAGE_PATH_2}/${encodeURIComponent(product?.id)}`);
              }}
            >
              View details
            </DropdownItem>
          </DropdownItemGroup>
        </DropdownMenu>
      );
    },
    [history, openSidebar, setBillDateSidebar]
  );

  const rowData = useMemo(() => {
    return (subscriptions ?? []).map(
      //@ts-ignore
      (product: generatedTypesNew.InvoiceGroupProduct) => {
        const {
          latestPeriodEndDate,
          billingFrequency,
          cloudSiteHostname,
          billedUsers,
          productId,
          entitlementNumber,
          trial,
          billingStatus,
          edition,
          productName,
          provisioningStatus,
          isSandbox,
        } = product;

        return {
          cells: [
            {
              content: (
                <CloudProductDetailView
                  productId={productId}
                  entitlementNumber={entitlementNumber || ''}
                  trial={trial ?? false}
                  billingStatus={billingStatus}
                  provisioningStatus={provisioningStatus}
                  edition={edition}
                  productName={productName}
                  isSandbox={isSandbox ?? false}
                  {...(isPartnerAccount && { productLink: `${subscriptionsRoutePPC}${entitlementNumber}` })}
                />
              ),
            },
            {
              content: isPaymentMethodSidebar ? (
                getSoldToAddress(product?.billingGroupId)
              ) : (
                <div className="line-clamp site-name-container" title={cloudSiteHostname ?? 'N/A'}>
                  {cloudSiteHostname ?? 'N/A'}
                </div>
              ),
            },
            {
              content: billingFrequency ? capitalize(billingFrequency) : 'N/A',
            },
            {
              content: (
                <div>
                  <FormattedDate date={latestPeriodEndDate} format={NEW_DATE_FORMAT} />
                  <NewBillDate entitlement={product as EntitlementDetails} />
                </div>
              ),
            },
            {
              content: (
                <div>
                  {billedUsers && <div>{capitalize(getBilledForColumnData(product?.productId, product?.edition))}</div>}
                  <div>{billedUsers ?? '-'}</div>
                </div>
              ),
            },
            {
              content: !isShipToPartySidebar && !isPaymentMethodSidebar && getRowAction(product),
            },
          ],
        };
      }
    );
  }, [getRowAction, subscriptions, isShipToPartySidebar, isPaymentMethodSidebar, getSoldToAddress]);

  const onPageChange = useCallback(
    (pageNumber: number) => {
      setCurrentPage(pageNumber);
      refetch({
        searchOptions: {
          offset: (pageNumber - 1) * rowsPerPage,
          first: rowsPerPage,
        },
        filterOptions: searchOptions.concat(filterOptions),
      });
    },
    [rowsPerPage, filterOptions, refetch, searchOptions]
  );

  const onSearch = useCallback(
    async (formData: FormData) => {
      const currentSearchOptions = [];
      const searchInput = formData?.searchTerm;
      if (searchInput) {
        currentSearchOptions.push({
          key: 'entitlementNumber',
          value: searchInput,
        });
      }
      setSearchOptions(currentSearchOptions);
      setCurrentPage(1);
      refetch({
        filterOptions: filterOptions.concat(currentSearchOptions),
        searchOptions: {
          first: rowsPerPage,
          offset: 0,
        },
      });
    },
    [refetch, filterOptions, rowsPerPage]
  );

  const onFilter = useCallback(
    (selectedFilters: { key: string; value: string }[]) => {
      setFilterOptions(selectedFilters);
      setCurrentPage(1);
      refetch({
        filterOptions: searchOptions.concat(selectedFilters),
        searchOptions: {
          first: rowsPerPage,
          offset: 0,
        },
      });
    },
    [refetch, searchOptions, rowsPerPage]
  );

  const handleRowsChange = useCallback(
    (newRowPerPage: number) => {
      if (newRowPerPage === rowsPerPage) {
        return;
      }
      setCurrentPage(1);
      setRowsPerPage(newRowPerPage);
      refetch({
        searchOptions: {
          first: newRowPerPage,
        },
        filterOptions: searchOptions.concat(filterOptions),
      });
    },
    [rowsPerPage, filterOptions, searchOptions, refetch]
  );

  if (error) {
    return (
      <div>
        <span className="search-status error-results" data-testid="error-row">
          <CrossCircleIcon label="Error" size="small" />
          Oh no! Something went wrong, please try again
          <span className="error">({error.toString()})</span>
        </span>
      </div>
    );
  }

  return (
    <DynamicResultTable
      headingText={label ?? 'Subscriptions'}
      head={headData}
      rows={rowData}
      emptyStateProps={{
        header: '',
        description: 'No subscriptions found',
      }}
      isLoading={loading}
      totalCount={totalCount}
      currentPage={currentPage}
      rowsPerPage={rowsPerPage}
      onPageChange={onPageChange}
      isPaginationEnabled={rowData && rowData.length > 0 && totalCount > DEFAULT_ROWS_PER_PAGE}
      onRowChange={handleRowsChange}
      showFilters={showFilters}
    >
      <div className="subscription-table-filter">
        <CustomSearch placeholder={'Search all linked subscriptions'} onSearch={onSearch} isLoading={loading} />
        <CustomFilterWrapper filterFields={FILTER_FIELDS} handleFilterUpdate={onFilter} />
      </div>
    </DynamicResultTable>
  );
};
